<template>
  <div v-if="hasAnyPredictions" class="w-100 mt-3">
    <!-- 📈 Итоговые агрегаты -->
    <div v-if="hasAggregates" class="mb-3 p-2 rounded border bg-light">
      <div class="fw-semibold text-secondary mb-1">📈 {{ $t('price_estimation.price_estimation.summary_title') }}</div>
      <div class="d-flex justify-content-between small text-dark">
        <span>{{ $t('price_estimation.price_estimation.avg_label') }}</span>
        <span class="fw-semibold">{{ formatValue(predictions.average_price) }}</span>
      </div>
      <div class="d-flex justify-content-between small text-dark">
        <span>{{ $t('price_estimation.price_estimation.median_label') }}</span>

        <span class="fw-semibold">{{ formatValue(predictions.median_price) }}</span>
      </div>
    </div>

    <!-- 🤖 Модели -->
    <div class="mb-2">
      <span class="fw-semibold text-secondary">🤖 {{ $t('price_estimation.price_estimation.models_title') }}</span>
    </div>

    <div
      v-for="(value, model, index) in sortedFilteredPredictions"
      :key="model"
      class="d-flex align-items-center mb-1"
      style="height: 22px;"
    >
      <div class="me-2 text-truncate text-muted" style="width: 120px; font-size: 0.75rem;">
        {{ model }}
      </div>

      <div
        class="flex-grow-1 position-relative"
        style="height: 6px; background: #e9ecef; border-radius: 4px; overflow: hidden;"
      >
        <div
          :style="{
            width: `${(value / maxValue * 100).toFixed(1)}%`,
            backgroundColor: colors[index % colors.length],
            height: '100%',
            transition: 'width 0.5s ease'
          }"
        ></div>
      </div>

      <div class="ms-2 text-end text-dark" style="width: 95px; font-size: 0.75rem;">
        {{ formatValue(value) }}
      </div>
    </div>
  </div>

  <div v-else class="text-muted small mt-2">
    🤖 {{ $t('price_estimation.price_estimation.not_ready') }}
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  predictions: {
    type: Object,
    required: true
  }
})

const colors = ['#6f42c1', '#20c997', '#fd7e14', '#0d6efd', '#d63384', '#198754']

// 🎯 фильтруем только числовые модели
const filteredPredictions = computed(() => {
  const result = {}
  if (!props.predictions) return result

  for (const [key, value] of Object.entries(props.predictions)) {
    if (
      typeof value === 'number' &&
      !isNaN(value) &&
      !['average_price', 'median_price'].includes(key)
    ) {
      result[key] = value
    }
  }
  return result
})

// 📊 сортировка по значению
const sortedFilteredPredictions = computed(() =>
  Object.entries(filteredPredictions.value)
    .sort((a, b) => b[1] - a[1])
    .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
)

// 🔝 максимум для прогресс-баров
const maxValue = computed(() => {
  const vals = Object.values(filteredPredictions.value)
  return vals.length ? Math.max(...vals) : 1
})

// 🧩 наличие данных
const hasAggregates = computed(
  () =>
    typeof props.predictions?.average_price === 'number' &&
    typeof props.predictions?.median_price === 'number'
)

const hasAnyPredictions = computed(
  () =>
    hasAggregates.value ||
    Object.keys(filteredPredictions.value).length > 0
)

// 💶 формат вывода
const formatValue = (val) => {
  if (typeof val !== 'number' || isNaN(val)) return '—'
  return `${val.toLocaleString('de-DE')} €`
}
</script>

<style scoped>
.bg-light {
  background-color: #f8f9fa !important;
}
.border {
  border: 1px solid #dee2e6 !important;
}
</style>
